import {
  distance as turfDistance,
  lineString as turfLineString,
  nearestPointOnLine,
  point as turfPoint,
  pointToLineDistance
} from '@turf/turf';
import {
  GraphhopperInstruction,
  JOIN_SOURCE,
  PROFILE_LINE,
  TurnInstruction,
  isJoinEntry
} from '../turnInstructions';
import { SPUR_MAX_METERS, capturedScale, isSnapped, pathLength, turnsBackAtJoin } from './joinSpurs';
import { isTerminal } from './maneuverSign';

/**
 * The turn at a marker, from GraphHopper.
 *
 * Each segment is routed marker to marker, so GraphHopper never sees what the route does at a
 * marker. Asked for a short route from 30 m before it to 30 m after, it routes straight through
 * and says what is there, including that nothing is.
 */

export { JOIN_SOURCE };

export const JOIN_WINDOW_METERS = 30;
/** Less route than this on either side, and the window's ends would land on the marker's turn. */
const MIN_WINDOW_METERS = 20;
/** GraphHopper's answer counts only if it drew our line. */
const ON_LINE_METERS = 3;
const LENGTH_TOLERANCE_METERS = 5;
/** Boundary slack for distances stored to a tenth of a metre. */
const EPSILON_METERS = 0.05;

export interface JoinSegment {
  coordinates: number[][];
  instructions: TurnInstruction[];
  profile?: string | null;
}

export interface JoinWindow {
  /** [lng, lat] to route from and to. */
  start: number[];
  end: number[];
  /** Our planned line between them. */
  line: number[][];
  waypointMeters: number;
  profile: string;
}

/** A turn GraphHopper reported, by metres from the marker: negative before it, positive after. */
export interface JoinTurn {
  offsetMeters: number;
  sign: number;
  text: string;
  street?: string;
}

export interface GraphhopperProbe {
  coordinates: number[][];
  instructions?: GraphhopperInstruction[];
}

/** Where to ask about the marker after `segments[joinIndex]`, or null when it can't be asked. */
export function joinWindow(segments: JoinSegment[], joinIndex: number): JoinWindow | null {
  const a = segments[joinIndex];
  const b = segments[joinIndex + 1];
  if (!a || !b) return null;

  const profile = a.profile;
  if (!profile || profile === PROFILE_LINE || b.profile !== profile) return null;
  if (!isSnapped(a) || !isSnapped(b)) return null;
  // A turnaround is ours to announce; GraphHopper would route straight past it.
  if (turnsBackAtJoin(a.coordinates, b.coordinates)) return null;

  const before = walk(segments, joinIndex, -1, profile);
  const after = walk(segments, joinIndex + 1, 1, profile);
  const waypointMeters = pathLength(before);
  if (waypointMeters < MIN_WINDOW_METERS || pathLength(after) < MIN_WINDOW_METERS) return null;

  const line = [...before.reverse(), ...after.slice(1)];
  return { start: line[0], end: line[line.length - 1], line, waypointMeters, profile };
}

/**
 * The turns GraphHopper reported within `SPUR_MAX_METERS` of the marker — an empty list when
 * it has none there — or null when its route isn't our line.
 */
export function joinTurnsFrom(window: JoinWindow, probe: GraphhopperProbe): JoinTurn[] | null {
  const coordinates = probe.coordinates ?? [];
  if (coordinates.length < 2) return null;

  const line = turfLineString(window.line.map((c) => [c[0], c[1]]));
  const offLine = coordinates.some(
    (c) => pointToLineDistance(turfPoint([c[0], c[1]]), line, { units: 'meters' }) > ON_LINE_METERS
  );
  if (offLine) return null;
  if (Math.abs(pathLength(coordinates) - pathLength(window.line)) > LENGTH_TOLERANCE_METERS) return null;

  const turns: JoinTurn[] = [];
  (probe.instructions ?? []).forEach((instruction, index) => {
    // The first describes the window's own start.
    if (index === 0) return;
    const sign = instruction.sign ?? 0;
    if (isTerminal(sign)) return;
    const at = coordinates[instruction.interval?.[0] ?? -1];
    if (!at) return;

    const along = nearestPointOnLine(line, turfPoint([at[0], at[1]]), { units: 'meters' }).properties.location;
    const offsetMeters = along - window.waypointMeters;
    if (Math.abs(offsetMeters) > SPUR_MAX_METERS) return;

    const turn: JoinTurn = { offsetMeters: round(offsetMeters), sign, text: instruction.text ?? '' };
    const street = instruction.street_name?.trim();
    if (street) turn.street = street;
    turns.push(turn);
  });
  return turns;
}

/**
 * A's and B's turns with GraphHopper's answer written in: within `SPUR_MAX_METERS` of the
 * marker it replaces what each segment saw on its own, and B's opening says the marker is
 * settled, so nothing is inferred there even when there is no turn. Null when a person's edit
 * sits in the way.
 */
export function withJoinTurns(
  a: JoinSegment,
  b: JoinSegment,
  turns: JoinTurn[]
): { a: TurnInstruction[]; b: TurnInstruction[] } | null {
  const near = nearJoin(a, b);
  if ([...near.a, ...near.b].some(isPersons)) return null;

  const aLength = pathLength(a.coordinates);
  const scaleA = capturedScale(a);
  const scaleB = capturedScale(b);
  const arrival = a.instructions.find((turn) => isTerminal(turn.sign))?.d ?? round(aLength * scaleA);

  const intoA = turns
    .filter((turn) => turn.offsetMeters <= 0)
    .map((turn) => written(turn, turn.offsetMeters === 0 ? arrival : round((aLength + turn.offsetMeters) * scaleA)));
  const intoB = turns
    .filter((turn) => turn.offsetMeters > 0)
    .map((turn) => written(turn, round(turn.offsetMeters * scaleB)));

  return settled(
    withEntries(
      a.instructions.filter((turn) => !near.a.includes(turn)),
      intoA,
      false
    ),
    withEntries(
      b.instructions.filter((turn) => !near.b.includes(turn)),
      intoB,
      true
    ),
    true
  );
}

/** A's and B's turns without anything written at the marker between them. */
export function withoutJoinTurns(a: JoinSegment, b: JoinSegment): { a: TurnInstruction[]; b: TurnInstruction[] } {
  const near = nearJoin(a, b);
  return settled(
    a.instructions.filter((turn) => !(isJoinEntry(turn) && near.a.includes(turn))),
    b.instructions.filter((turn) => !(isJoinEntry(turn) && near.b.includes(turn))),
    false
  );
}

/** A's arrival and B's opening, the bookends either side of the marker, marked settled or not. */
function settled(a: TurnInstruction[], b: TurnInstruction[], isSettled: boolean): { a: TurnInstruction[]; b: TurnInstruction[] } {
  const mark = (turn: TurnInstruction): TurnInstruction => {
    const { src: _previous, ...rest } = turn;
    return isSettled ? { ...rest, src: JOIN_SOURCE } : rest;
  };
  const arrival = a.map((turn) => isTerminal(turn.sign)).lastIndexOf(true);
  return {
    a: a.map((turn, index) => (index === arrival ? mark(turn) : turn)),
    b: b.map((turn, index) => (index === 0 ? mark(turn) : turn))
  };
}

/** A marker between two line strings, as they were when GraphHopper was asked about it. */
export interface JoinTarget {
  aId: string;
  bId: string;
  aEnd: number[];
  bStart: number[];
}

interface LineStringLike {
  lineStringGeometry: { coordinates: number[][] };
  lineStringProperties: { id?: string | null; instructions?: TurnInstruction[]; profile?: string | null };
}

export function joinSegmentOf(lineString: LineStringLike): JoinSegment {
  return {
    coordinates: lineString.lineStringGeometry?.coordinates ?? [],
    instructions: lineString.lineStringProperties?.instructions ?? [],
    profile: lineString.lineStringProperties?.profile
  };
}

export function joinTarget(lineStrings: LineStringLike[], joinIndex: number): JoinTarget | null {
  const a = lineStrings[joinIndex];
  const b = lineStrings[joinIndex + 1];
  const aCoordinates = a?.lineStringGeometry?.coordinates ?? [];
  const bCoordinates = b?.lineStringGeometry?.coordinates ?? [];
  const aId = a?.lineStringProperties?.id;
  const bId = b?.lineStringProperties?.id;
  if (!aId || !bId || aCoordinates.length === 0 || bCoordinates.length === 0) return null;
  return { aId, bId, aEnd: aCoordinates[aCoordinates.length - 1], bStart: bCoordinates[0] };
}

/**
 * Writes `turns` at the marker `target` names, or clears what was written there when null.
 * Nothing changes if the line either side has been replaced or trimmed since. Returns whether
 * anything did.
 */
export function applyJoinTurns(lineStrings: LineStringLike[], target: JoinTarget, turns: JoinTurn[] | null): boolean {
  const k = lineStrings.findIndex((ls) => ls.lineStringProperties?.id === target.aId);
  if (k < 0) return false;
  const current = joinTarget(lineStrings, k);
  if (
    !current ||
    current.bId !== target.bId ||
    !samePoint(current.aEnd, target.aEnd) ||
    !samePoint(current.bStart, target.bStart)
  ) {
    return false;
  }

  const a = joinSegmentOf(lineStrings[k]);
  const b = joinSegmentOf(lineStrings[k + 1]);
  const result = turns === null ? withoutJoinTurns(a, b) : withJoinTurns(a, b, turns);
  if (!result) return false;
  if (JSON.stringify(result.a) === JSON.stringify(a.instructions) && JSON.stringify(result.b) === JSON.stringify(b.instructions)) {
    return false;
  }
  lineStrings[k].lineStringProperties.instructions = result.a;
  lineStrings[k + 1].lineStringProperties.instructions = result.b;
  return true;
}

function samePoint(p: number[], q: number[]): boolean {
  return p[0] === q[0] && p[1] === q[1];
}

/** The entries of A and B within `SPUR_MAX_METERS` of the marker, bookends aside. */
function nearJoin(a: JoinSegment, b: JoinSegment): { a: TurnInstruction[]; b: TurnInstruction[] } {
  const aLength = pathLength(a.coordinates);
  const scaleA = capturedScale(a);
  const scaleB = capturedScale(b);
  return {
    a: a.instructions.filter(
      (turn) => !isTerminal(turn.sign) && turn.d / scaleA >= aLength - SPUR_MAX_METERS - EPSILON_METERS
    ),
    b: b.instructions.filter(
      (turn, index) => index > 0 && !isTerminal(turn.sign) && turn.d / scaleB <= SPUR_MAX_METERS + EPSILON_METERS
    )
  };
}

function isPersons(turn: TurnInstruction): boolean {
  if (isJoinEntry(turn)) return false;
  return turn.cue != null || !!turn.utext || turn.usign != null || !!turn.hidden;
}

function written(turn: JoinTurn, d: number): TurnInstruction {
  const entry: TurnInstruction = { d, sign: turn.sign, text: turn.text, src: JOIN_SOURCE };
  if (turn.street) entry.street = turn.street;
  return entry;
}

/** `kept` with `added` placed by distance, ahead of the bookends that must stay at the ends. */
function withEntries(kept: TurnInstruction[], added: TurnInstruction[], keepsOpening: boolean): TurnInstruction[] {
  const opening = keepsOpening && kept.length > 0 ? [kept[0]] : [];
  const rest = keepsOpening ? kept.slice(1) : kept;
  const body = [...rest.filter((turn) => !isTerminal(turn.sign)), ...added].sort((x, y) => x.d - y.d);
  return [...opening, ...body, ...rest.filter((turn) => isTerminal(turn.sign))];
}

/**
 * Up to `JOIN_WINDOW_METERS` of route from the marker, through segment `from` and on in the
 * direction of `step`, stopping at anything not routed with the same profile.
 */
function walk(segments: JoinSegment[], from: number, step: number, profile: string): number[][] {
  const path: number[][] = [];
  let length = 0;
  for (let k = from; k >= 0 && k < segments.length; k += step) {
    const segment = segments[k];
    if (segment.profile !== profile || !isSnapped(segment)) break;
    const coordinates = step < 0 ? [...segment.coordinates].reverse() : segment.coordinates;
    for (const c of coordinates) {
      const previous = path[path.length - 1];
      if (!previous) {
        path.push(c);
        continue;
      }
      const d = meters(previous, c);
      if (d === 0) continue;
      if (length + d >= JOIN_WINDOW_METERS) {
        const t = (JOIN_WINDOW_METERS - length) / d;
        path.push([previous[0] + (c[0] - previous[0]) * t, previous[1] + (c[1] - previous[1]) * t]);
        return path;
      }
      length += d;
      path.push(c);
    }
  }
  return path;
}

function meters(p: number[], q: number[]): number {
  return turfDistance([p[0], p[1]], [q[0], q[1]], { units: 'meters' });
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
